import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grow
} from '@mui/material';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pricingOptions = [
  {
    title: 'Free Trial',
    price: '$0',
    description: '7-day trial',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
    ],
    buttonText: 'Start Free Trial',
    buttonVariant: 'outlined',
  },
  {
    title: 'Monthly',
    price: '$4.99',
    description: '/ month',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
      'Cancel anytime',
    ],
    buttonText: 'Subscribe Monthly',
    buttonVariant: 'contained',
  },
  {
    title: 'Yearly',
    price: '$50',
    description: '/ year',
    features: [
      'Personalized learning paths',
      'AI-powered recommendations',
      'Access to all content',
      'Community support',
      'Cancel anytime',
      '2 months free!',
    ],
    buttonText: 'Subscribe Yearly',
    buttonVariant: 'outlined',
    bestValue: true,
  },
];

const PricingPage = ({ onSubscribe, onBack }) => {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = async (plan) => {
    try {
      await onSubscribe(plan);
      setShowSuccessDialog(true);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to process subscription');
      setShowErrorDialog(true);
    }
  };

  // Card style to match About/Contact
  const aboutCardStyle = {
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 4,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    p: 4,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  const cardBaseStyle = {
    ...aboutCardStyle,
    border: '1.5px solid rgba(102,126,234,0.15)',
  };
  const popularCardStyle = {
    ...aboutCardStyle,
    border: '2.5px solid #FF8E53',
    position: 'relative',
    overflow: 'visible',
  };

  const cardContentStyles = {
    textAlign: 'center',
    p: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  };

  // Gradient button style
  const gradientButtonStyles = {
    fontWeight: 'bold',
    borderRadius: '8px',
    py: 1.5,
    color: '#fff',
    px: 0,
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 16px 0 rgba(102,126,234,0.15)',
    textTransform: 'none',
    fontSize: 18,
    letterSpacing: 1.2,
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.3s, box-shadow 0.3s',
    '&:hover': {
      background: 'linear-gradient(90deg, #764ba2 0%, #667eea 100%)',
      boxShadow: '0 6px 24px 0 rgba(102,126,234,0.25)'
    }
  };

  // Sheen animation for Best Value button
  const sheenKeyframes = `
    0% { left: -75%; }
    60% { left: 125%; }
    100% { left: 125%; }
  `;

  const bestValueButtonStyles = {
    ...gradientButtonStyles,
    '&:hover::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: '50%',
      background: 'linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 100%)',
      zIndex: 1,
      pointerEvents: 'none',
      animation: `${sheenKeyframes} 0.8s cubic-bezier(0.4,0,0.2,1)`
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-75%',
      height: '100%',
      width: '50%',
      background: 'linear-gradient(120deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.05) 100%)',
      zIndex: 1,
      pointerEvents: 'none',
      transition: 'left 0.3s',
    }
  };

  // Helper for gradient text
  const gradientText = {
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 8,
      bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            textAlign: 'center', 
            fontWeight: 'bold', 
            color: '#fff', 
            mb: 2,
            animation: `${fadeIn} 0.5s ease-out 0.2s forwards`,
            opacity: 0
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center', 
            color: 'rgba(255, 255, 255, 0.8)', 
            mb: 6,
            maxWidth: '600px',
            mx: 'auto',
            animation: `${fadeIn} 0.5s ease-out 0.4s forwards`,
            opacity: 0
          }}
        >
          Unlock your potential with personalized AI recommendations. Start building the perfect college application today.
        </Typography>
        <Grid container spacing={5} justifyContent="center" alignItems="stretch">
          {pricingOptions.map((option, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Grow in={true} timeout={(index + 1) * 200}>
                <Card sx={{ ...(option.bestValue ? popularCardStyle : cardBaseStyle), minHeight: 420 }}>
                  {option.bestValue && (
                    <Chip label="Best Value" color="secondary" size="small" sx={{ position: 'absolute', top: 16, right: 16 }} />
                  )}
                  <CardContent sx={{ textAlign: 'center', p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography component="h2" variant="h4" sx={{ mt: 2, color: '#222', fontWeight: 700 }}>
                      {option.title}
                    </Typography>
                    <Box display="flex" justifyContent="center" alignItems="flex-start">
                      <Typography component="h3" variant="h3" sx={{ fontWeight: 700, ...gradientText }}>
                        {option.price}
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#222', ml: 1, mt: 2 }}>
                        {option.description}
                      </Typography>
                    </Box>
                    <ul style={{ paddingLeft: 0, listStyle: 'none', margin: '16px 0' }}>
                      {option.features.map((feature, i) => (
                        <Typography key={i} variant="body1" sx={{ mb: 1, color: '#222', opacity: 0.9 }}>
                          {feature}
                        </Typography>
                      ))}
                    </ul>
                    <Box sx={{ mt: 'auto' }}>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={option.bestValue ? bestValueButtonStyles : gradientButtonStyles}
                        onClick={() => handleSubscribe(option.title.toLowerCase())}
                      >
                        {option.buttonText}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Dialogs */}
      <Dialog open={showSuccessDialog} onClose={() => {
        setShowSuccessDialog(false);
        onBack();
      }}>
        <DialogTitle>Subscription Successful!</DialogTitle>
        <DialogContent>
          <Typography>
            Welcome to Pathly! You now have access to all our AI-powered recommendations and features.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowSuccessDialog(false);
            onBack();
          }}>Continue</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)}>
        <DialogTitle>Subscription Error</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
          <Typography>
            Please try again or contact support if the problem persists.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPage; 